import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchReferralInfo, fetchRewards } from '@/services/api/affiliateService';
import styles from './History.module.css';

const ReferralHistory = ({ referralCode: propReferralCode }) => {
  // Fetch referral code if not provided
  const { data: referralInfo } = useQuery({
    queryKey: ['affiliate', 'referralInfo'],
    queryFn: fetchReferralInfo,
    enabled: !propReferralCode,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    gcTime: 10 * 60 * 1000,
  });

  const referralCode = propReferralCode || referralInfo?.referralCode;

  // Fetch rewards (only if referral code exists)
  const {
    data: rewardsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['affiliate', 'rewards', referralCode],
    queryFn: () => fetchRewards(referralCode),
    enabled: !!referralCode,
    staleTime: 5 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    gcTime: 10 * 60 * 1000,
  });

  // Filter referral rewards
  const referrals = React.useMemo(() => {
    if (!rewardsData?.rewards) return [];
    return rewardsData.rewards.filter((reward) => reward.type === 'referral');
  }, [rewardsData]);

  if (isLoading) {
    return <div className={styles.loadingSpinner}>Loading...</div>;
  }

  if (error) {
    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyText}>Failed to fetch referral history</div>
      </div>
    );
  }

  if (!referrals || referrals.length === 0) {
    return (
      <div className={styles.emptyState}>
        <img
          src="/assets/images/bcgames/affiliate/empty.webp"
          alt="No rewards"
          className={styles.emptyImage}
        />
        <div className={styles.emptyText}>
          No rewards yet. <br /> Invite friends to join you now!
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <table className={styles.dataTable}>
        <thead>
          <tr>
            <th>Member Name</th>
            <th>Date</th>
            <th>Referral Code</th>
            <th style={{ textAlign: 'right' }}>Amount (FC)</th>
          </tr>
        </thead>
        <tbody>
          {referrals.map((referral, index) => (
            <tr key={index}>
              <td>{referral.membername || 'N/A'}</td>
              <td>{new Date(referral.createdAt).toLocaleDateString()}</td>
              <td>{referral.referalCode || 'N/A'}</td>
              <td style={{ textAlign: 'right' }}>
                <span className={styles.amountPositive}>
                  {referral.referalmount || 0}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ReferralHistory;
